import { AnalyticsEvents } from '@app/analytics/events';
import { useAnalytics } from '@app/analytics/use-analytics';
import { useFlag } from '@app/feature-flags/use-flag';
import { logger } from '@app/routes/apps/utils';
import { FLAG_ENABLE_EDR_ANALYTICS_WITH_TRACK_AND_FLUSH } from '@app/utilities/flags';
import { MouseEvent, useCallback } from 'react';
import { NavigateOptions, useNavigate } from 'react-router-dom';

class TimeoutError extends Error {}

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new TimeoutError()), ms),
    ),
  ]);

// max blocking time for analytics call in ms
const TIMEOUT = 300;

export const useTrackedLinkHandler = (
  event: AnalyticsEvents,
  payload?: Record<string, any>,
) => {
  const { track, trackAndFlush } = useAnalytics();
  const isTrackAndFlushEnabled = useFlag(
    FLAG_ENABLE_EDR_ANALYTICS_WITH_TRACK_AND_FLUSH,
  );

  return useCallback(
    async (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const href = e.currentTarget.href;
      try {
        if (isTrackAndFlushEnabled) {
          // Use trackAndFlush - sends immediately via sendBeacon
          await trackAndFlush(event, payload);
          logger.info('Event tracked and flushed, navigating to:', href);
        } else {
          // Fallback to regular track with timeout
          await withTimeout(track(event, payload), TIMEOUT);
        }
      } catch (error) {
        if (error instanceof TimeoutError) {
          logger.warning('Analytics call timed out');
        } else {
          logger.error('Error in analytics tracking:', error);
        }
      } finally {
        // Navigate - sendBeacon will complete even after navigation starts
        window.location.assign(href);
      }
    },
    [event, payload, track, trackAndFlush, isTrackAndFlushEnabled],
  );
};

export const useTrackedRedirectHandler = (
  event: AnalyticsEvents,
  url: string,
  payload?: Record<string, any>,
) => {
  const { track, trackAndFlush } = useAnalytics();
  const isTrackAndFlushEnabled = useFlag(
    FLAG_ENABLE_EDR_ANALYTICS_WITH_TRACK_AND_FLUSH,
  );

  return useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        if (isTrackAndFlushEnabled) {
          // Use trackAndFlush - sends immediately via sendBeacon
          await trackAndFlush(event, payload);
          logger.info('Event tracked and flushed, navigating to:', url);
        } else {
          // Fallback to regular track with timeout
          await withTimeout(track(event, payload), TIMEOUT);
        }
      } catch (error) {
        if (error instanceof TimeoutError) {
          logger.warning('Analytics call timed out');
        } else {
          logger.error('Error in analytics tracking:', error);
        }
      } finally {
        // Navigate - sendBeacon will complete even after navigation starts
        window.location.assign(url);
      }
    },
    [event, url, payload, track, trackAndFlush, isTrackAndFlushEnabled],
  );
};

export const useTrackedNavigateHandler = (
  event: AnalyticsEvents,
  to: string,
  payload?: Record<string, any>,
  navigateOptions?: NavigateOptions,
) => {
  const { track, trackAndFlush } = useAnalytics();
  const isTrackAndFlushEnabled = useFlag(
    FLAG_ENABLE_EDR_ANALYTICS_WITH_TRACK_AND_FLUSH,
  );
  const navigate = useNavigate();

  return useCallback(
    async (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      try {
        if (isTrackAndFlushEnabled) {
          // Use trackAndFlush for better reliability during navigation
          await trackAndFlush(event, payload);
          logger.info('Event tracked and flushed, navigating to:', to);
        } else {
          // Fallback to regular track with timeout
          await withTimeout(track(event, payload), TIMEOUT);
        }
      } catch (error) {
        if (error instanceof TimeoutError) {
          logger.warning('Analytics call timed out');
        } else {
          logger.error('Error in analytics tracking:', error);
        }
      } finally {
        if (to && to !== '#') {
          navigate(to, navigateOptions);
        }
      }
    },
    [
      event,
      to,
      payload,
      navigateOptions,
      track,
      trackAndFlush,
      isTrackAndFlushEnabled,
      navigate,
    ],
  );
};
